/**
 * ManageGroupsModal — create / rename / delete company groups, and
 * bulk-assign companies into a group.
 *
 * Groups in this system aren't a separate table — they're the value
 * of Company.group_name. So:
 *   * Creating a group = naming it + assigning at least one company.
 *   * Renaming = updating every company that shares the name.
 *   * Deleting = clearing the name on every member (companies stay,
 *     they're just no longer tagged).
 *
 * Backend wiring:
 *   GET    /api/v1/company-groups                      — list
 *   POST   /api/v1/company-groups/rename               — {old_name, new_name}
 *   POST   /api/v1/company-groups/assign               — {group_name, company_ids[]}
 *   POST   /api/v1/company-groups/remove               — {company_ids[]}
 *   DELETE /api/v1/company-groups/{name}               — disband
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Loader2, Plus, Edit2, Trash2, Users, ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Typeahead from './Typeahead';


interface GroupRow {
  name: string;
  count: number;
}

interface CompanyOption {
  id: number;
  name: string;
  industry?: string;
  group_name?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;  // parent refresh after any mutation
  initialMode?: 'list' | 'create';
}

const ManageGroupsModal: React.FC<Props> = ({
  open, onClose, onChanged, initialMode = 'list',
}) => {
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(false);

  const [mode, setMode] = useState<'list' | 'create' | 'rename' | 'assign'>(
    initialMode,
  );
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [memberFilter, setMemberFilter] = useState('');

  // Lazy-load when opened.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    (async () => {
      setLoading(true);
      try {
        const [gRes, cRes] = await Promise.all([
          api.get('/company-groups').catch(() => null),
          api.get('/companies', { params: { size: 500, page: 1 } }).catch(() => null),
        ]);
        setGroups(gRes?.data?.groups || []);
        setCompanies(
          (cRes?.data?.companies || []).map((c: any) => ({
            id: c.id, name: c.name, industry: c.industry,
            group_name: c.group_name,
          })),
        );
      } finally {
        setLoading(false);
      }
    })();
    setActiveGroup(null);
    setNewName('');
    setPicked(new Set());
    setMemberFilter('');
  }, [open, initialMode]);

  const togglePick = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredCompanies = useMemo(() => {
    const q = memberFilter.trim().toLowerCase();
    const sorted = companies.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    if (!q) return sorted;
    return sorted.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.industry || '').toLowerCase().includes(q),
    );
  }, [companies, memberFilter]);

  const refreshGroups = async () => {
    const r = await api.get('/company-groups').catch(() => null);
    if (r?.data?.groups) setGroups(r.data.groups);
    onChanged();
  };

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) { toast.error('Enter a group name'); return; }
    if (picked.size === 0) { toast.error('Pick at least one company'); return; }
    setSubmitting(true);
    try {
      await api.post('/company-groups/assign', {
        group_name: name,
        company_ids: Array.from(picked),
      });
      toast.success(`Group "${name}" — ${picked.size} compan${picked.size === 1 ? 'y' : 'ies'} assigned`);
      await refreshGroups();
      setMode('list');
      setNewName('');
      setPicked(new Set());
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to create group');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRename = async () => {
    if (!activeGroup) return;
    const target = newName.trim();
    if (!target) { toast.error('Enter a new name'); return; }
    if (target.toLowerCase() === activeGroup.toLowerCase()) {
      toast('Same name — nothing to rename.', { icon: 'ℹ️' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/company-groups/rename', {
        old_name: activeGroup,
        new_name: target,
      });
      toast.success(`Renamed — ${r.data?.renamed ?? 0} compan${(r.data?.renamed ?? 0) === 1 ? 'y' : 'ies'} updated`);
      await refreshGroups();
      setMode('list');
      setActiveGroup(null);
      setNewName('');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAssignToExisting = async () => {
    if (!activeGroup) return;
    if (picked.size === 0) { toast.error('Pick at least one company'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/company-groups/assign', {
        group_name: activeGroup,
        company_ids: Array.from(picked),
      });
      toast.success(
        `Added ${r.data?.assigned ?? picked.size} compan${(r.data?.assigned ?? picked.size) === 1 ? 'y' : 'ies'} to "${activeGroup}"`,
      );
      await refreshGroups();
      setMode('list');
      setActiveGroup(null);
      setPicked(new Set());
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Assign failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (name: string) => {
    if (!window.confirm(`Disband group "${name}"? Companies are kept, just untagged.`)) return;
    setSubmitting(true);
    try {
      const r = await api.delete(`/company-groups/${encodeURIComponent(name)}`);
      toast.success(`Disbanded — ${r.data?.removed ?? 0} compan${(r.data?.removed ?? 0) === 1 ? 'y' : 'ies'} untagged`);
      await refreshGroups();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {mode === 'create' && 'Create new group'}
              {mode === 'rename' && `Rename "${activeGroup}"`}
              {mode === 'assign' && `Add companies to "${activeGroup}"`}
              {mode === 'list' && 'Manage company groups'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Groups tag companies for cohort reports, filtering, and
              dashboards. A company can belong to one group at a time.
            </p>
          </div>
          <button
            onClick={() => !submitting && onClose()}
            className="p-1 text-gray-400 hover:text-gray-700"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading groups…
            </div>
          ) : mode === 'list' ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  Existing groups ({groups.length})
                </span>
                <button
                  onClick={() => { setMode('create'); setNewName(''); setPicked(new Set()); }}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg"
                >
                  <Plus className="w-3.5 h-3.5" /> New group
                </button>
              </div>
              {groups.length === 0 ? (
                <div className="border border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
                  No groups yet. Click <strong>+ New group</strong> to create one,
                  or import a CSV with a "Group" column to auto-create on upload.
                </div>
              ) : (
                <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {groups.map((g) => (
                    <li key={g.name} className="flex items-center gap-3 px-4 py-3">
                      <Users className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{g.name}</div>
                        <div className="text-xs text-gray-500">
                          {g.count} compan{g.count === 1 ? 'y' : 'ies'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setActiveGroup(g.name); setMode('assign'); setPicked(new Set()); }}
                          className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                          title="Add companies to this group"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setActiveGroup(g.name); setMode('rename'); setNewName(g.name); }}
                          className="p-1.5 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                          title="Rename group"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(g.name)}
                          className="p-1.5 text-gray-500 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Disband group (companies untagged)"
                          disabled={submitting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : mode === 'rename' ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  New name for "{activeGroup}"
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <p className="text-xs text-gray-500">
                Every company currently in <strong>{activeGroup}</strong> will be
                updated to use the new name.
              </p>
            </div>
          ) : (
            // create or assign mode — both pick companies; create also takes a name
            <div className="space-y-3">
              {mode === 'create' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Group name
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="e.g. MTW Spring 2026"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-700">
                    Pick companies ({picked.size} selected)
                  </label>
                  <button
                    type="button"
                    onClick={() => setPicked(new Set())}
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    Clear
                  </button>
                </div>
                <input
                  type="search"
                  placeholder="Filter by name or industry…"
                  value={memberFilter}
                  onChange={(e) => setMemberFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                />
                <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                  {filteredCompanies.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-gray-500 text-center">No matches.</div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {filteredCompanies.slice(0, 200).map((c) => {
                        const checked = picked.has(c.id);
                        return (
                          <li key={c.id}>
                            <label className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50/50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => togglePick(c.id)}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                                {c.industry && (
                                  <div className="text-xs text-gray-500 truncate">{c.industry}</div>
                                )}
                              </div>
                              {c.group_name && c.group_name !== activeGroup && (
                                <span className="text-[10px] uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                  in {c.group_name}
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {filteredCompanies.length > 200 && (
                  <p className="text-xs text-gray-400 italic mt-1">
                    Showing first 200 — narrow your filter to see more.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl flex-shrink-0">
          <button
            onClick={() => mode === 'list' ? onClose() : setMode('list')}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-white rounded-lg disabled:opacity-50"
          >
            {mode === 'list' ? 'Close' : '← Back'}
          </button>
          {mode === 'create' && (
            <button
              onClick={submitCreate}
              disabled={submitting || !newName.trim() || picked.size === 0}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create group ({picked.size})
            </button>
          )}
          {mode === 'rename' && (
            <button
              onClick={submitRename}
              disabled={submitting || !newName.trim()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Rename
            </button>
          )}
          {mode === 'assign' && (
            <button
              onClick={submitAssignToExisting}
              disabled={submitting || picked.size === 0}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Add {picked.size}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageGroupsModal;
